import { useState } from "react";
import {
  Building2,
  ChartNoAxesCombined,
  ChevronDown,
  CircleUserRound,
  Ellipsis,
  Gift,
  Home,
  ListChecks,
  LogOut,
  Settings2,
  ShieldCheck,
  Sun,
  LifeBuoy,
  UserCog,
  UserPlus,
  Users,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { roleLabel } from "@/lib/api";
import { DEV_TOOLS_ENABLED } from "@/hooks/useAuth";
import { ProfileDialog } from "@/components/ProfileDialog";

const ROLE_ICONS = {
  customer: CircleUserRound,
  ambassador: ChartNoAxesCombined,
  trade_partner: Building2,
  admin: ShieldCheck,
};

const MEMBER_TABS = [
  ["home", "Home", Home],
  ["refer", "Refer", Users],
  ["rewards", "Rewards", Gift],
  // Visible to members with a linked plant (plant → member mapping in backend).
  ["monitoring", "Monitoring", Sun],
  ["support", "Support", LifeBuoy],
];

const CUSTOMER_TABS = [
  ["home", "Home", Home],
  ["refer", "Refer", Users],
  ["rewards", "Rewards", Gift],
  ["monitoring", "My Solar", Sun],
  ["support", "Support", LifeBuoy],
];

const ADMIN_TABS = [
  ["overview", "Overview", ChartNoAxesCombined],
  ["applications", "Applications", UserPlus],
  ["members", "Members", Users],
  ["rules", "Reward Rules", Settings2],
  ["operations", "Operations", ListChecks],
  ["staff", "Staff", UserCog],
  ["solar-sites", "Solar Sites", Building2],
  // Plant monitoring is admin-only for rollout stage 3 (see migration plan v2).
  ["monitoring", "Monitoring", Sun],
];

const PRIMARY_ADMIN_TABS = [
  ADMIN_TABS[0],
  ADMIN_TABS[1],
  ADMIN_TABS[2],
  ADMIN_TABS[4],
];

const MORE_ADMIN_TABS = [
  ADMIN_TABS[3],
  ADMIN_TABS[5],
  ADMIN_TABS[6],
  ADMIN_TABS[7],
];

const ROLES = ["customer", "ambassador", "trade_partner", "admin"];

export const tabsForRole = (role) => {
  if (role === "admin") return ADMIN_TABS;
  if (role === "customer") return CUSTOMER_TABS;
  return MEMBER_TABS;
};

export const mobileTabsForRole = (role) =>
  role === "admin"
    ? { primary: PRIMARY_ADMIN_TABS, more: MORE_ADMIN_TABS }
    : { primary: tabsForRole(role), more: [] };

export const tabKeysForRole = (role) => tabsForRole(role).map((tab) => tab[0]);
export const defaultTabForRole = (role) =>
  (role === "admin" ? "overview" : "home");

const Brand = ({ compact = false }) => (
  <div className={`flex items-center gap-2.5 ${compact ? "lg:hidden" : "mb-10 px-2"}`}>
    <img src="/logo-mark.png" alt="Greentek Alliance" className={compact ? "h-9 w-9" : "h-11 w-11"} />
    <div className={compact ? "hidden leading-[1.08] min-[380px]:block" : "leading-[1.08]"}>
      <span className="block text-[15px] font-bold uppercase tracking-[0.08em] text-brand-ink">Greentek</span>
      <span className="block text-[15px] font-bold uppercase tracking-[0.08em] text-brand-gold">Alliance</span>
    </div>
  </div>
);

const NavigationItems = ({ tabs, mobile = false }) => {
  const navigate = useNavigate();
  const current = useLocation().pathname.replace(/^\//, "");
  return (
    <>
      {tabs.map(([key, label, Icon]) => {
        const active = current === key;
        const desktopClass = active
          ? "bg-brand-gold-soft text-brand-gold-deep shadow-[inset_3px_0_0_#c9a460]"
          : "text-brand-muted hover:bg-black/[.035] hover:text-brand-ink";
        const mobileClass = active ? "bg-brand-gold-light text-brand-ink" : "text-white/60";
        return (
          <button
            key={key}
            data-testid={`${mobile ? "mobile-nav" : "nav"}-${key}`}
            aria-current={mobile && active ? "page" : undefined}
            onClick={() => navigate(`/${key}`)}
            className={mobile
              ? `flex min-h-12 min-w-0 flex-1 flex-col items-center justify-center gap-1 rounded-[14px] text-[11px] transition-[background-color,color] ${mobileClass}`
              : `flex min-h-11 w-full items-center gap-3 rounded-[13px] px-3 text-sm font-medium transition-[background-color,color,box-shadow] ${desktopClass}`}
          >
            <Icon size={mobile ? 19 : 18} />
            {mobile ? <span className="max-w-full truncate">{label}</span> : label}
          </button>
        );
      })}
    </>
  );
};

const Sidebar = ({ tabs }) => (
  <aside className="fixed inset-y-0 left-0 z-30 hidden w-[248px] border-r border-black/5 bg-white/75 px-4 py-6 backdrop-blur-xl lg:block" data-testid="desktop-sidebar">
    <Brand />
    <p className="mb-3 px-3 text-[11px] font-bold uppercase tracking-[.18em] text-brand-subtle">Workspace</p>
    <nav className="space-y-1"><NavigationItems tabs={tabs} /></nav>
  </aside>
);

const RoleSwitcher = ({ role, setRole }) => {
  const RoleIcon = ROLE_ICONS[role];
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          data-testid="role-switcher"
          aria-label={`View as ${roleLabel(role)}`}
          className="h-11 w-11 rounded-[13px] border-black/10 bg-white/80 px-0 shadow-sm sm:w-auto sm:px-3"
        >
          <RoleIcon size={16} /><span className="hidden sm:inline">View as</span><b className="hidden max-w-[120px] truncate sm:inline">{roleLabel(role)}</b><ChevronDown className="hidden sm:block" size={14} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 rounded-[14px] p-1">
        {ROLES.map((item) => {
          const Icon = ROLE_ICONS[item];
          return <DropdownMenuItem key={item} data-testid={`role-${item}`} onClick={() => setRole(item)} className="min-h-11 rounded-[10px]"><Icon className="mr-2" size={17} />{roleLabel(item)}</DropdownMenuItem>;
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const Topbar = ({ role, setRole, onLogout }) => (
  <header className="sticky top-0 z-20 border-b border-black/5 bg-white/60 pt-[env(safe-area-inset-top)] backdrop-blur-xl" data-testid="topbar">
    <div className="mx-auto flex min-h-[72px] max-w-[1500px] flex-nowrap items-center justify-between gap-2 px-4 py-2 md:px-7">
      <Brand compact />
      <div className="hidden lg:block"><p className="text-xs text-brand-muted">Welcome back</p><h1 className="brand-title text-xl font-semibold">{roleLabel(role)}</h1></div>
      <div className="flex items-center gap-2">
        {DEV_TOOLS_ENABLED && <RoleSwitcher role={role} setRole={setRole} />}
        <ProfileDialog role={role} onLogout={onLogout} />
        <Button
          variant="outline"
          data-testid="logout-button"
          aria-label="Sign out"
          onClick={onLogout}
          className="h-11 w-11 rounded-[13px] border-black/10 bg-white/80 px-0 shadow-sm sm:w-auto sm:px-3"
        >
          <LogOut size={16} /><span className="hidden sm:inline">Sign out</span>
        </Button>
      </div>
    </div>
  </header>
);

const MobileNav = ({ role }) => {
  const navigate = useNavigate();
  const current = useLocation().pathname.replace(/^\//, "");
  const [moreOpen, setMoreOpen] = useState(false);
  const { primary, more } = mobileTabsForRole(role);
  const activeMoreTab = more.find(([key]) => key === current);
  const moreClass = activeMoreTab
    ? "bg-brand-gold-light text-brand-ink"
    : "text-white/60";

  const selectMoreTab = (key) => {
    navigate(`/${key}`);
    setMoreOpen(false);
  };

  return (
    <nav className="fixed inset-x-0 bottom-0 z-40 pb-[env(safe-area-inset-bottom)] lg:hidden" data-testid="mobile-navigation">
      <div className="mx-3 mb-3 flex h-[66px] items-center justify-start gap-1 rounded-[20px] border border-white/60 bg-[#092522]/95 px-2 text-white shadow-2xl backdrop-blur-xl">
        <NavigationItems tabs={primary} mobile />
        {more.length > 0 && (
          <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="nav-more"
                aria-current={activeMoreTab ? "page" : undefined}
                className={`flex min-h-12 min-w-0 flex-1 flex-col items-center justify-center gap-1 rounded-[14px] text-[11px] transition-[background-color,color] ${moreClass}`}
              >
                <Ellipsis size={19} />
                <span className="max-w-full truncate">{activeMoreTab?.[1] || "More"}</span>
              </button>
            </SheetTrigger>
            <SheetContent
              side="bottom"
              data-testid="nav-more-sheet"
              className="rounded-t-[20px] pb-[calc(1.5rem+env(safe-area-inset-bottom))]"
            >
              <SheetHeader>
                <SheetTitle className="brand-title text-left">More</SheetTitle>
              </SheetHeader>
              <div className="mt-2 space-y-1">
                {more.map(([key, label, Icon]) => (
                  <button
                    key={key}
                    data-testid={`nav-more-${key}`}
                    aria-current={current === key ? "page" : undefined}
                    onClick={() => selectMoreTab(key)}
                    className="flex min-h-12 w-full items-center gap-3 rounded-[13px] px-3 text-left text-sm font-medium text-brand-ink hover:bg-black/[.035]"
                  >
                    <Icon size={18} />
                    <span>{label}</span>
                  </button>
                ))}
              </div>
            </SheetContent>
          </Sheet>
        )}
      </div>
    </nav>
  );
};

export const AppShell = ({ role, setRole, onLogout, children }) => {
  const tabs = tabsForRole(role);
  return (
    <div className="min-h-screen bg-brand-canvas text-brand-ink">
      <Sidebar tabs={tabs} />
      <div className="lg:pl-[248px]">
        <Topbar role={role} setRole={setRole} onLogout={onLogout} />
        <main className="mx-auto max-w-[1500px] px-4 pb-[calc(6.5rem+env(safe-area-inset-bottom))] pt-6 md:px-7 md:pt-8 lg:pb-10">{children}</main>
      </div>
      <MobileNav role={role} />
    </div>
  );
};
